import { Link } from "react-router-dom";
import { displayName, leadTime, shortMoney, sizeFromName } from "../lib/format";
import type { ProductSummary } from "../lib/types";
import { DimensionLine, Sticker } from "./brand";

/**
 * A product sitting on the page, not in a box: the photo carries the card, and the
 * text underneath stays short — name, price, and one practical line.
 */
export function ProductCard({ product, feature = false, showCategory = true }: {
  product: ProductSummary; feature?: boolean; showCategory?: boolean;
}) {
  const soldOut = product.stockQuantity === 0;
  const size = sizeFromName(product.name);
  const ready = product.productType === "READY_MADE";
  const href = `/products/${product.slug}`;
  return (
    <article className="group relative flex flex-col">
      <Link to={href} className="relative block" tabIndex={-1} aria-hidden="true">
        <div className={`relative aspect-square overflow-hidden rounded-[6px] bg-frosting ${soldOut ? "opacity-60 grayscale-[.4]" : ""}`}>
          <img
            src={product.imageUrl}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover transition duration-700 ease-settle group-hover:scale-[1.035]"
          />
        </div>
        <span className="absolute -top-2 left-3 flex gap-1.5">
          {soldOut && <Sticker tone="cocoa" tilt={-4}>Sold out</Sticker>}
          {!soldOut && product.bestseller && <Sticker tone="butter" tilt={-4} className="transition-transform duration-300 group-hover:!rotate-2">Best seller</Sticker>}
          {!soldOut && ready && <Sticker tone="mint" tilt={product.bestseller ? 3 : -3}>Ready to ship</Sticker>}
        </span>
      </Link>

      {feature ? <div className="mt-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          {showCategory && <p className="mb-1 text-[13px] font-medium text-muted">{displayName(product.category.name)}</p>}
          <h3 className="font-display text-[1.6rem] leading-[1.1] text-cocoa md:text-[2rem]"><TitleLink href={href} name={product.name} /></h3>
        </div>
        <Price product={product} large />
      </div> : <div className="mt-3">
        {showCategory && <p className="mb-0.5 text-[12px] font-medium text-muted">{displayName(product.category.name)}</p>}
        <h3 className="text-[15px] font-medium leading-snug text-cocoa"><TitleLink href={href} name={product.name} /></h3>
        <Price product={product} />
      </div>}

      {(size || !ready) && <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-muted">
        {size && <DimensionLine label={size} tone="cocoa" className="w-[84px] shrink-0" />}
        {!ready && <span>Made in {leadTime(product.estimatedDays)}</span>}
      </div>}
    </article>
  );
}

function TitleLink({ href, name }: { href: string; name: string }) {
  return <Link to={href} className="decoration-ribbon decoration-[1.5px] underline-offset-4 after:absolute after:inset-0 after:content-[''] group-hover:underline">{name}</Link>;
}

function Price({ product, large = false }: { product: ProductSummary; large?: boolean }) {
  return (
    <p className={`flex items-baseline gap-2 ${large ? "shrink-0 flex-col items-end gap-1 pt-1" : "mt-1"}`}>
      <span className={`font-display leading-none text-cocoa ${large ? "text-2xl" : "text-lg"}`}>{shortMoney(product.basePriceCentavos)}</span>
      {product.compareAtPriceCentavos && <span className="text-[12px] text-muted line-through">{shortMoney(product.compareAtPriceCentavos)}</span>}
    </p>
  );
}
